import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { ExerciseResponse } from '@src/data/exercise-response'
import { ensureGifLoops } from '@src/util/gif-loop'

const DEFAULT_PICTURE = '/assets/icon/brand/default-exercise.jpg'

/**
 * Preview of an exercise card: the GIF, patched in the browser so that it loops (some GIFs lack the loop marker),
 * the video when there is no GIF, or the default picture. If the GIF cannot be patched it is shown as it is.
 */
@Component
export class ExercisePreview extends Vue {

  @Prop({ required: true })
  exercise: ExerciseResponse

  private objectUrl = ''
  private failed = false
  private gone = false

  get videoURLs (): { webm, mp4, gif } {
    // an exercise loaded without its videos has no preview
    return this.exercise.videos ? this.exercise.getVideoURLs() : { webm: '', mp4: '', gif: '' }
  }

  get gifUrl (): string {
    return this.videoURLs.gif
  }

  @Watch('gifUrl', { immediate: true })
  async load () {
    this.release()
    this.failed = false
    const gif = this.gifUrl
    if (!gif) return

    try {
      const response = await fetch(gif, { mode: 'cors', credentials: 'omit' })
      if (!response.ok) throw new Error('gif not loaded')
      const bytes = ensureGifLoops(new Uint8Array(await response.arrayBuffer()))
      const url = URL.createObjectURL(new Blob([bytes], { type: 'image/gif' }))
      if (this.gone || gif !== this.gifUrl) {
        URL.revokeObjectURL(url)
        return
      }
      this.objectUrl = url
    } catch (error) {
      // a late failure of a GIF that is no longer shown must not touch the current one
      if (gif === this.gifUrl) this.failed = true
    }
  }

  beforeDestroy () {
    this.gone = true
    this.release()
  }

  release () {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = ''
  }

  showOriginal () {
    this.release()
    this.failed = true
  }

  render (h: CreateElement) {
    const videoURLs = this.videoURLs
    if (videoURLs.gif) {
      if (this.objectUrl) return <img key='looping' class='exercise-preview' src={this.objectUrl} onError={this.showOriginal} />
      if (this.failed) return <img key='original' class='exercise-preview' src={videoURLs.gif} attrs-onerror={`this.src="${DEFAULT_PICTURE}"`} />
      return <img key='loading' class='exercise-preview' />
    }
    if (videoURLs.mp4 || videoURLs.webm) {
      return <video class='exercise-preview' autoplay='autoplay' loop='loop' poster={DEFAULT_PICTURE} muted>
        <source src={videoURLs.webm} type='video/webm' />
        <source src={videoURLs.mp4} type='video/mp4' />
        Your browser does not support the video tag.
      </video>
    }
    return <img class='exercise-preview' src={DEFAULT_PICTURE} />
  }
}
